import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Chart from "@/v2-ui/chart"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type {
  RenewalBucket,
  RenewalGranularity,
} from "@/v2-backoffice-client/license/renewals/license.renewals.utils"

// Brand data colors, not theme chrome (charts can't read CSS vars). The
// series wear the same colors as the event badges in the table below.
const CHART_CONFIG = {
  renewing: { label: "Renewing", color: DATA_VIZ.brand.green2 },
  starting: { label: "Starting", color: DATA_VIZ.brand.greenBright },
  atRisk: { label: "At risk", color: DATA_VIZ.brand.amber },
  churning: { label: "Churning", color: DATA_VIZ.semantic.negative },
}

const RUN_RATE_COLOR = DATA_VIZ.semantic.neutral

export type RenewalChartMode = "cash" | "cumulative" | "runrate"

const MODE_OPTIONS: { label: string; value: RenewalChartMode }[] = [
  { label: "Cash flow", value: "cash" },
  { label: "Cumulative", value: "cumulative" },
  { label: "Run-rate", value: "runrate" },
]

const GRANULARITY_OPTIONS: { label: string; value: RenewalGranularity }[] = [
  { label: "Monthly", value: "month" },
  { label: "Quarterly", value: "quarter" },
]

const HORIZON_OPTIONS: { label: string; value: number }[] = [
  { label: "12 mo", value: 12 },
  { label: "24 mo", value: 24 },
]

const MODE_BLURB: Record<RenewalChartMode, string> = {
  cash: "What lands each period — renewals and starts above the line, the cash churn takes with it below. Yearly contracts spike in their renewal month; monthlies flow steadily. Click a bar to focus the table.",
  cumulative:
    "The same events added up: how much cash the window has generated by each period, against the total that churn has taken. The gap is the book's real intake.",
  runrate:
    "The committed book's monthly billing over time: today's run-rate, plus scheduled subscriptions as they start, minus churned ones as their final term lapses.",
}

export type LicenseRenewalChartProps = {
  buckets: RenewalBucket[]
  currency: string
  mode: RenewalChartMode
  onModeChange(mode: RenewalChartMode): void
  granularity: RenewalGranularity
  onGranularityChange(granularity: RenewalGranularity): void
  horizonMonths: number
  onHorizonChange(months: number): void
  /** The clicked bucket (toggles); null = nothing selected. */
  selectedBucketKey: string | null
  onBucketClick(bucketKey: string): void
}

// The cashflow forecast in three views — per-period cash (stacked bars with
// churn below the axis), cumulative intake vs loss (gradient areas), and the
// run-rate trajectory — with monthly/quarterly bucketing and a 12/24-month
// horizon. Clicking a period filters the events table under the chart.
function LicenseRenewalChart(props: LicenseRenewalChartProps) {
  const {
    buckets,
    currency,
    mode,
    onModeChange,
    granularity,
    onGranularityChange,
    horizonMonths,
    onHorizonChange,
    selectedBucketKey,
    onBucketClick,
  } = props

  const selectedBucket = buckets.find(
    (bucket) => bucket.key === selectedBucketKey,
  )

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <ChartChips
          options={MODE_OPTIONS}
          value={mode}
          onChange={onModeChange}
        />
        <ChartChips
          options={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={onGranularityChange}
        />
        <ChartChips
          options={HORIZON_OPTIONS}
          value={horizonMonths}
          onChange={onHorizonChange}
        />

        <div className="flex-1" />

        {mode !== "runrate" && (
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            {Object.values(CHART_CONFIG).map((series) => (
              <span key={series.label} className="flex items-center gap-1">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: series.color }}
                />
                {series.label}
              </span>
            ))}
            {mode === "cash" && (
              <span className="flex items-center gap-1">
                <span
                  className="h-0.5 w-3 rounded-full"
                  style={{ backgroundColor: RUN_RATE_COLOR }}
                />
                Run-rate
              </span>
            )}
          </span>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {selectedBucket
          ? `Table focused on ${selectedBucket.label} — click the bar again to clear.`
          : MODE_BLURB[mode]}
      </p>

      <Chart config={CHART_CONFIG} className="h-[280px] w-full">
        <Chart.ComposedChart
          data={buckets}
          margin={{ left: -4, right: 4, top: 4 }}
          onClick={(state) => {
            const index = state?.activeTooltipIndex
            const bucket =
              typeof index === "number" ? buckets[index] : undefined
            if (bucket && mode === "cash") onBucketClick(bucket.key)
          }}
        >
          <defs>
            <linearGradient id="renewal-grad-in" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={DATA_VIZ.brand.green2}
                stopOpacity={0.75}
              />
              <stop
                offset="100%"
                stopColor={DATA_VIZ.brand.green2}
                stopOpacity={0.08}
              />
            </linearGradient>
            <linearGradient id="renewal-grad-churn" x1="0" y1="1" x2="0" y2="0">
              <stop
                offset="0%"
                stopColor={DATA_VIZ.semantic.negative}
                stopOpacity={0.7}
              />
              <stop
                offset="100%"
                stopColor={DATA_VIZ.semantic.negative}
                stopOpacity={0.06}
              />
            </linearGradient>
            <linearGradient id="renewal-grad-rate" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={DATA_VIZ.brand.green}
                stopOpacity={0.5}
              />
              <stop
                offset="100%"
                stopColor={DATA_VIZ.brand.green}
                stopOpacity={0.04}
              />
            </linearGradient>
          </defs>

          <Chart.Grid vertical={false} />
          <Chart.Axis.X
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={6}
            minTickGap={12}
            className="text-xs text-muted-foreground"
          />
          <Chart.Axis.Y
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            width={64}
            domain={mode === "runrate" ? ["auto", "auto"] : undefined}
            tickFormatter={(value: number) => formatCompactAmount(value, null)}
            className="text-xs text-muted-foreground"
          />
          <Chart.Tooltip
            content={<RenewalBucketTooltip currency={currency} mode={mode} />}
          />

          {mode === "cash" && selectedBucket && (
            <Chart.ReferenceArea
              x1={selectedBucket.label}
              x2={selectedBucket.label}
              fill={DATA_VIZ.brand.green}
              fillOpacity={0.08}
              stroke={DATA_VIZ.brand.green}
              strokeOpacity={0.3}
            />
          )}

          {mode === "cash" && (
            <>
              <Chart.Bar
                dataKey="renewing"
                stackId="cash"
                fill="var(--color-renewing)"
                maxBarSize={28}
                className="cursor-pointer"
              />
              <Chart.Bar
                dataKey="starting"
                stackId="cash"
                fill="var(--color-starting)"
                maxBarSize={28}
                className="cursor-pointer"
              />
              <Chart.Bar
                dataKey="atRisk"
                stackId="cash"
                fill="var(--color-atRisk)"
                radius={[3, 3, 0, 0]}
                maxBarSize={28}
                className="cursor-pointer"
              />
              <Chart.Bar
                dataKey="churning"
                stackId="cash"
                fill="var(--color-churning)"
                radius={[0, 0, 3, 3]}
                maxBarSize={28}
                className="cursor-pointer"
              />
              <Chart.Line
                dataKey="runRate"
                stroke={RUN_RATE_COLOR}
                strokeWidth={2}
                dot={false}
                type="monotone"
              />
            </>
          )}

          {mode === "cumulative" && (
            <>
              <Chart.Area
                dataKey="cumulativeIn"
                stroke={DATA_VIZ.brand.green2}
                strokeWidth={2}
                fill="url(#renewal-grad-in)"
                type="monotone"
              />
              <Chart.Area
                dataKey="cumulativeChurn"
                stroke={DATA_VIZ.semantic.negative}
                strokeWidth={2}
                fill="url(#renewal-grad-churn)"
                type="monotone"
              />
            </>
          )}

          {mode === "runrate" && (
            <Chart.Area
              dataKey="runRate"
              stroke={DATA_VIZ.brand.green}
              strokeWidth={2.5}
              fill="url(#renewal-grad-rate)"
              type="stepAfter"
              dot={{ r: 2.5, fill: DATA_VIZ.brand.green, strokeWidth: 0 }}
            />
          )}
        </Chart.ComposedChart>
      </Chart>
    </section>
  )
}

// The house segmented-chip control (the Activity page's app filter), local
// and generic over the option value.
function ChartChips<T extends string | number>(props: {
  options: { label: string; value: T }[]
  value: T
  onChange(value: T): void
}) {
  const { options, value, onChange } = props
  return (
    <div className="inline-flex items-center overflow-hidden rounded-md border border-border text-xs">
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={cn(
            "px-3 py-1 transition-colors",
            value === option.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted",
          )}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

// Mode-aware hover: cash mode itemizes the period, cumulative mode shows
// intake vs loss so far, run-rate mode the trajectory point.
function RenewalBucketTooltip(props: {
  active?: boolean
  payload?: { payload: RenewalBucket }[]
  currency?: string | null
  mode: RenewalChartMode
}) {
  const { active, payload, currency, mode } = props
  const bucket = payload?.[0]?.payload
  if (!active || !bucket) return null

  const cashIn = bucket.renewing + bucket.starting + bucket.atRisk
  const parts = [
    { label: "Renewing", value: bucket.renewing },
    { label: "Starting", value: bucket.starting },
    { label: "At risk", value: bucket.atRisk },
    { label: "Churning", value: bucket.churning },
  ].filter((part) => part.value !== 0)

  return (
    <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      <p className="font-medium">
        {bucket.label} · {bucket.eventCount} event
        {bucket.eventCount === 1 ? "" : "s"}
      </p>

      {mode === "cumulative" ? (
        <>
          <p className="text-muted-foreground">
            {formatCompactAmount(bucket.cumulativeIn, currency)} generated so
            far
          </p>
          <p className="text-muted-foreground">
            {formatCompactAmount(bucket.cumulativeChurn, currency)} lost to
            churn so far
          </p>
          <p className="mt-1 text-muted-foreground">
            Net{" "}
            {formatCompactAmount(
              bucket.cumulativeIn + bucket.cumulativeChurn,
              currency,
            )}
          </p>
        </>
      ) : mode === "runrate" ? (
        <p className="text-muted-foreground">
          Run-rate {formatCompactAmount(bucket.runRate, currency)}/mo
        </p>
      ) : (
        <>
          <p className="text-muted-foreground">
            {formatCompactAmount(cashIn, currency)} expected in
          </p>
          {parts.map((part) => (
            <p key={part.label} className="text-muted-foreground">
              {part.label} {formatNumber(part.value)}
            </p>
          ))}
          <p className="mt-1 text-muted-foreground">
            Run-rate after: {formatCompactAmount(bucket.runRate, currency)}/mo
          </p>
        </>
      )}
    </div>
  )
}

export default LicenseRenewalChart
